/**
 * Merchant Webhook Card
 *
 * Inline webhook config card on the merchant detail page.
 * Shows URL, masked secret, status, updatedAt with action buttons.
 * Reuses existing dialog components from the webhook-config feature.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, RotateCw, Zap, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
  Badge, Button, Spinner,
} from '@/components/ui';
import {
  WebhookConfigDialog,
  SecretRevealDialog,
  RotateSecretConfirmation,
  TestWebhookResult,
} from '@/components/features/webhook-config';
import {
  useWebhookConfig,
  useSetWebhookConfig,
  useDeleteWebhookConfig,
  useRotateWebhookSecret,
  useTestWebhook,
} from '@/hooks/use-webhook-config';
import { formatDate } from '@/lib/utils';
import type { WebhookTestResult } from '@/types/webhook-config';

interface MerchantWebhookCardProps {
  merchantId: string;
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

export function MerchantWebhookCard({ merchantId }: MerchantWebhookCardProps) {
  const { t } = useTranslation('webhook-config');

  const { data: config, isLoading, error } = useWebhookConfig(merchantId);
  const setConfigMutation = useSetWebhookConfig();
  const deleteMutation = useDeleteWebhookConfig();
  const rotateMutation = useRotateWebhookSecret();
  const testMutation = useTestWebhook();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const [secretRevealOpen, setSecretRevealOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState('');
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);

  // 404 = no webhook configured -- treat as empty state, not error
  const isNotFound = error && 'status' in error && (error as { status: number }).status === 404;
  const hasNonNotFoundError = !!error && !isNotFound;
  const hasConfig = !!config && !isNotFound;
  const showEmpty = !isLoading && !hasNonNotFoundError && (!hasConfig || isNotFound);

  const handleEditSubmit = async (_merchantId: string, webhookUrl: string) => {
    try {
      const result = await setConfigMutation.mutateAsync({ merchantId, webhookUrl });
      // 201 response returns cleartext secret (new config created)
      if (result.hmacSecret && !result.hmacSecret.includes('****')) {
        setRevealedSecret(result.hmacSecret);
        setSecretRevealOpen(true);
      }
      toast.success(t('card.editSuccess', { defaultValue: 'Webhook updated' }));
      setEditDialogOpen(false);
    } catch {
      toast.error(t('card.editError', { defaultValue: 'Failed to update webhook' }));
    }
  };

  const handleRotateConfirm = async () => {
    try {
      const result = await rotateMutation.mutateAsync(merchantId);
      setRotateDialogOpen(false);
      if (result.hmacSecret) {
        setRevealedSecret(result.hmacSecret);
        setSecretRevealOpen(true);
      }
    } catch {
      toast.error(t('card.rotateError', { defaultValue: 'Failed to rotate secret' }));
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync(merchantId);
      setTestResult(result);
    } catch {
      toast.error(t('card.testError', { defaultValue: 'Failed to test webhook' }));
    }
  };

  const handleToggleEnabled = async () => {
    if (!config) return;

    if (config.enabled) {
      // Disable: soft-delete via DELETE endpoint
      try {
        await deleteMutation.mutateAsync(merchantId);
        toast.success(t('card.toggle.disableSuccess', { defaultValue: 'Webhook disabled' }));
      } catch {
        toast.error(t('card.toggle.disableError', { defaultValue: 'Failed to disable webhook' }));
      }
    } else {
      // Enable: PUT with existing URL to re-activate
      try {
        const result = await setConfigMutation.mutateAsync({
          merchantId,
          webhookUrl: config.webhookUrl,
        });
        // Handle 201 (new secret created) vs 200 (just re-enabled)
        if (result.hmacSecret && !result.hmacSecret.includes('****')) {
          setRevealedSecret(result.hmacSecret);
          setSecretRevealOpen(true);
        }
        toast.success(t('card.toggle.enableSuccess', { defaultValue: 'Webhook enabled' }));
      } catch {
        toast.error(t('card.toggle.enableError', { defaultValue: 'Failed to enable webhook' }));
      }
    }
  };

  const isToggling = deleteMutation.isPending || setConfigMutation.isPending;

  if (isLoading) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">{t('card.title', { defaultValue: 'Webhook Configuration' })}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-6">
            <Spinner size="md" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state: no webhook configured
  if (showEmpty) {
    return (
      <>
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">{t('card.title', { defaultValue: 'Webhook Configuration' })}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="py-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t('card.empty.title', { defaultValue: 'No webhook configured' })}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('card.empty.description', { defaultValue: 'Set up a webhook to receive event notifications.' })}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setEditDialogOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('card.empty.configure', { defaultValue: 'Configure Webhook' })}
              </Button>
            </div>
          </CardContent>
        </Card>

        <WebhookConfigDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          mode="create"
          merchantId={merchantId}
          onSubmit={handleEditSubmit}
          isLoading={setConfigMutation.isPending}
        />

        <SecretRevealDialog
          open={secretRevealOpen}
          onOpenChange={setSecretRevealOpen}
          secret={revealedSecret}
          onConfirm={() => {
            setSecretRevealOpen(false);
            setRevealedSecret('');
          }}
        />
      </>
    );
  }

  // Non-404 error
  if (hasNonNotFoundError) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">{t('card.title', { defaultValue: 'Webhook Configuration' })}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            {t('card.loadError', { defaultValue: 'Failed to load webhook configuration.' })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mt-6">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">{t('card.title', { defaultValue: 'Webhook Configuration' })}</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditDialogOpen(true)}
            >
              <Link2 className="mr-1.5 h-3.5 w-3.5" />
              {t('card.actions.edit', { defaultValue: 'Edit URL' })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRotateDialogOpen(true)}
            >
              <RotateCw className="mr-1.5 h-3.5 w-3.5" />
              {t('card.actions.rotate', { defaultValue: 'Rotate Secret' })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleTest}
              disabled={testMutation.isPending}
            >
              <Zap className="mr-1.5 h-3.5 w-3.5" />
              {testMutation.isPending
                ? t('card.actions.testing', { defaultValue: 'Testing...' })
                : t('card.actions.test', { defaultValue: 'Test' })}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <InfoField label={t('card.url', { defaultValue: 'Webhook URL' })}>
              <p className="break-all font-mono text-sm">{config!.webhookUrl}</p>
            </InfoField>
            <InfoField label={t('card.secret', { defaultValue: 'HMAC Secret' })}>
              <p className="font-mono text-sm">{config!.hmacSecret}</p>
            </InfoField>
            <InfoField label={t('card.status', { defaultValue: 'Status' })}>
              <div className="flex items-center gap-3">
                <Badge variant={config!.enabled ? 'success' : 'warning'}>
                  {config!.enabled ? t('status.active') : t('status.inactive')}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleEnabled}
                  disabled={isToggling}
                  className="h-7 text-xs"
                >
                  {isToggling
                    ? '...'
                    : config!.enabled
                      ? t('card.actions.disable', { defaultValue: 'Disable' })
                      : t('card.actions.enable', { defaultValue: 'Enable' })}
                </Button>
              </div>
            </InfoField>
            <InfoField label={t('card.updatedAt', { defaultValue: 'Last Updated' })}>
              <p className="text-sm">{formatDate(config!.updatedAt)}</p>
            </InfoField>
          </div>

          {/* Inline test result */}
          {testResult && (
            <div className="mt-2">
              <TestWebhookResult result={testResult} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <WebhookConfigDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        mode="edit"
        merchantId={merchantId}
        currentUrl={config?.webhookUrl}
        onSubmit={handleEditSubmit}
        isLoading={setConfigMutation.isPending}
      />

      <RotateSecretConfirmation
        open={rotateDialogOpen}
        onOpenChange={setRotateDialogOpen}
        merchantId={merchantId}
        onConfirm={handleRotateConfirm}
        isRotating={rotateMutation.isPending}
      />

      <SecretRevealDialog
        open={secretRevealOpen}
        onOpenChange={setSecretRevealOpen}
        secret={revealedSecret}
        onConfirm={() => {
          setSecretRevealOpen(false);
          setRevealedSecret('');
        }}
      />
    </>
  );
}
